import {
  BUSINESS_HUB_PATH,
  PLAYLISTS_PATH,
  TITLES_PATH,
} from "../../services/postAuthRouting";
import { isBetaPath } from "../../config/betaSurfaces";
import { NAV_ICON_COMPONENTS, renderNavIcon } from "./navIcons";

function createNavItem({ iconComponent, ...item }) {
  return {
    ...item,
    // Derived, not passed in: a new Streaming entry cannot be added without
    // the mark, and unmarking one is a single edit in betaSurfaces.
    beta: isBetaPath(item.to),
    iconComponent,
    icon: renderNavIcon(iconComponent),
  };
}

export function buildNavSections({
  isAdmin,
  isSelected,
  isBusinessUser,
  isIndividualUser,
  hasActiveBusinessContext,
  canAccessCollections,
  canAccessTimeCapsules,
}) {
  // Invited collaborators/managers keep an individual account while working
  // inside a business. The route guards (RequireRaffleAccess and
  // RequireCollectionAccess, both via resolveBusinessGate) already admit them,
  // so gating nav on accountType alone hid tools they could still reach by URL.
  const isInvitedBusinessMember =
    !isAdmin && !isBusinessUser && Boolean(hasActiveBusinessContext);
  // Raffles/coupons ride on the `tv` scope, mirroring RequireRaffleAccess.
  const canManageRaffleCampaigns = Boolean(
    isBusinessUser || (isInvitedBusinessMember && canAccessCollections),
  );
  const canManageCollections = isAdmin || canAccessCollections;
  const canManageTimeCapsules =
    isAdmin || isBusinessUser || canAccessTimeCapsules;
  const canUseMomentMaker =
    isAdmin ||
    isBusinessUser ||
    (isInvitedBusinessMember && canAccessTimeCapsules);
  const showTimeCapsuleSeederNav = false;

  return [
    {
      title: "Business",
      items:
        isBusinessUser ||
        isAdmin ||
        isIndividualUser ||
        hasActiveBusinessContext
          ? [
              createNavItem({
                to: BUSINESS_HUB_PATH,
                label:
                  isIndividualUser && !isAdmin && !hasActiveBusinessContext
                    ? "Become a business"
                    : "Business Hub",
                sublabel: isAdmin
                  ? "Manage businesses and active context"
                  : isIndividualUser && !hasActiveBusinessContext
                    ? "Upgrade and complete business onboarding"
                    : "Company profile, team, and access",
                iconComponent: NAV_ICON_COMPONENTS.businessHub,
                selected: isSelected(BUSINESS_HUB_PATH),
              }),
            ]
          : [],
    },
    {
      title: "Campaigns",
      items: [
        ...(canManageTimeCapsules
          ? [
              createNavItem({
                to: "/time-capsules",
                label: "Time Capsules",
                sublabel: "Create and manage capsules",
                iconComponent: NAV_ICON_COMPONENTS.timeCapsules,
                selected: isSelected("/time-capsules"),
              }),
              ...(canUseMomentMaker
                ? [
                    createNavItem({
                      to: "/moment-maker",
                      label: "Moment Maker",
                      sublabel: "AI ideas for sponsored capsules",
                      iconComponent: NAV_ICON_COMPONENTS.momentMaker,
                      selected: isSelected("/moment-maker"),
                    }),
                  ]
                : []),
            ]
          : []),
        ...(isAdmin
          ? [
              createNavItem({
                to: "/challenges",
                label: "Challenges",
                sublabel: "Create and manage challenges",
                iconComponent: NAV_ICON_COMPONENTS.challenges,
                selected: isSelected("/challenges"),
              }),
              createNavItem({
                to: "/challenge-maker",
                label: "Challenge Maker",
                sublabel: "AI challenge ideas with your levers",
                iconComponent: NAV_ICON_COMPONENTS.challengeMaker,
                selected: isSelected("/challenge-maker"),
              }),
            ]
          : []),
      ],
    },
    {
      title: "Offers",
      items: [
        ...(canManageRaffleCampaigns
          ? [
              createNavItem({
                to: "/raffles",
                label: "Digital Raffles",
                sublabel: "Create, manage, and redeem raffles",
                iconComponent: NAV_ICON_COMPONENTS.raffles,
                selected: isSelected("/raffles"),
              }),
              createNavItem({
                to: "/coupons",
                label: "Digital Coupons",
                sublabel: "Create, manage, and redeem coupons",
                iconComponent: NAV_ICON_COMPONENTS.coupons,
                selected: isSelected("/coupons"),
              }),
            ]
          : []),
      ],
    },
    {
      title: "Streaming",
      items: [
        createNavItem({
          to: "/content",
          label: "Content",
          sublabel: "Create and manage content",
          iconComponent: NAV_ICON_COMPONENTS.content,
          selected: isSelected("/content"),
        }),
        ...(canManageCollections || isAdmin
          ? [
              ...(canManageCollections
                ? [
                    createNavItem({
                      to: PLAYLISTS_PATH,
                      label: "Playlists",
                      sublabel: "Curate sets, reuse everywhere",
                      iconComponent: NAV_ICON_COMPONENTS.playlists,
                      selected: isSelected(PLAYLISTS_PATH),
                    }),
                    createNavItem({
                      to: "/inssa-live",
                      label: "Inssa Live",
                      sublabel: "Manage TVs by location",
                      iconComponent: NAV_ICON_COMPONENTS.inssaLive,
                      selected: isSelected("/inssa-live"),
                    }),
                  ]
                : []),
              ...(isAdmin
                ? [
                    createNavItem({
                      to: TITLES_PATH,
                      label: "Titles",
                      sublabel: "Series, movies and collections",
                      iconComponent: NAV_ICON_COMPONENTS.titles,
                      selected: isSelected(TITLES_PATH),
                    }),
                    createNavItem({
                      to: "/tv-rails",
                      label: "TV Rows",
                      sublabel: "What appears on TV",
                      iconComponent: NAV_ICON_COMPONENTS.tvRails,
                      disabled: true,
                      selected: isSelected("/tv-rails"),
                    }),
                  ]
                : []),
            ]
          : []),
      ],
    },
    {
      title: "Insights",
      items:
        isAdmin || isBusinessUser || hasActiveBusinessContext
          ? [
              ...(isAdmin
                ? [
                    createNavItem({
                      to: "/admin/analytics",
                      label: "Executive Analytics",
                      sublabel: "Businesses, conversion, and benchmarks",
                      iconComponent: NAV_ICON_COMPONENTS.analytics,
                      selected: isSelected("/admin/analytics"),
                    }),
                  ]
                : []),
              ...(!isAdmin || hasActiveBusinessContext
                ? [
                    createNavItem({
                      to: "/analytics",
                      label: "Campaign Analytics",
                      sublabel: "Campaigns, conversion, and collaborators",
                      iconComponent: NAV_ICON_COMPONENTS.analytics,
                      selected: isSelected("/analytics"),
                    }),
                  ]
                : []),
            ...(showTimeCapsuleSeederNav
              ? [
                  createNavItem({
                    to: "/admin/timecapsules",
                    label: "Capsule Search Tokens",
                    sublabel: "Backfill the search index",
                    iconComponent: NAV_ICON_COMPONENTS.timeCapsuleSeeder,
                    selected: isSelected("/admin/timecapsules"),
                  }),
                ]
              : []),
            ]
          : [],
    },
    {
      title: "Admin Tools",
      items: isAdmin
        ? [
            createNavItem({
              to: "/admin/settings",
              label: "Platform Settings",
              sublabel: "Global app configuration",
              iconComponent: NAV_ICON_COMPONENTS.platformSettings,
              selected: isSelected("/admin/settings"),
            }),
            createNavItem({
              to: "/admin/users",
              label: "Users",
              sublabel: "Profiles, identity, and Auth",
              iconComponent: NAV_ICON_COMPONENTS.users,
              selected: isSelected("/admin/users"),
            }),
            createNavItem({
              to: "/admin/campaigns",
              label: "Push Campaigns",
              sublabel: "Notification campaigns",
              iconComponent: NAV_ICON_COMPONENTS.campaigns,
              selected: isSelected("/admin/campaigns"),
            }),
            createNavItem({
              to: "/admin/points",
              label: "Insider Points",
              sublabel: "Point rules and triggers",
              iconComponent: NAV_ICON_COMPONENTS.points,
              selected: isSelected("/admin/points"),
            }),
            createNavItem({
              to: "/admin/promos",
              label: "Promo Codes",
              sublabel: "Point-awarding promo campaigns",
              iconComponent: NAV_ICON_COMPONENTS.promoCodes,
              selected: isSelected("/admin/promos"),
            }),
            createNavItem({
              to: "/admin/sponsored-pin-template",
              label: "Pin Maker",
              sublabel: "Fit logos and publish the shared pin",
              iconComponent: NAV_ICON_COMPONENTS.pinTemplates,
              selected: isSelected("/admin/sponsored-pin-template"),
            }),
          ]
        : [],
    },
  ];
}
